import { differenceInSeconds, format, isBefore, parseISO } from 'date-fns';

const getItemLinks = (value: any): string[] => {
  if (!value) return [];

  return String(value)
    .split(',')
    .map((link) => link.trim())
    .filter((link) => link.startsWith('http://') || link.startsWith('https://'));
};

export const getChecklistFileEntries = (data: any) => {
  const rawItens = Array.isArray(data?.itens) ? data.itens : Array.isArray(data?.items) ? data.items : [];

  return rawItens.flatMap((item: any, idx: number) => {
    const links = getItemLinks(item?.file_url || item?.fileUrl || item?.answer);

    return links.map((link) => ({
      ...item,
      idx,
      file_url: link,
      fileUrl: link,
    }));
  });
};

const validarItens = (itens: any) => {
  const invalidItems = [];

  for (const item of itens) {
    if (item.critical_question !== 1) continue;

    let rules: any;
    try {
      rules = JSON.parse(item.rules.rule_json);
    } catch (e) {
      continue; 
    }

    let isInvalid = false;

    if (item.rules.question_type === 'choiceQuestion') {
      if (rules.invalid_values) {
        const answer = rules.normalize
          ? item.answer?.toLowerCase().trim()
          : item.answer?.trim();

        const invalids = rules.invalid_values.map((v: any) =>
          rules.normalize ? v.toLowerCase() : v
        );

        if (invalids.includes(answer)) {
          isInvalid = true;
        }
      }
    }

    if (item.rules.question_type === 'dateQuestion') {
      if (rules.invalid_if_before === 'today') {
        const answerDate = parseISO(item.answer);
        const createdAt = parseISO(item.created_at);

        if (isBefore(answerDate, createdAt)) {
          isInvalid = true;
        }
      }
    }

    if (isInvalid) invalidItems.push(item);
  }

  return invalidItems; // retorna todos os inválidos
};

const sanitizeChecklistText = (value: any) => {
  if (value == null) return '';

  return String(value)
    .replace(/.*Pergunta Cr[ií]tica.*$/gim, '')
    .replace(/.*Viola[cç][aã]o Cr[ií]tica Detectada.*$/gim, '')
    .replace(/\n{2,}/g, '\n')
    .trim();
};

export const isChecklistItemInvalid = (item: any) => validarItens([item]).length > 0;
export const getSanitizedChecklistText = sanitizeChecklistText;

export const getChecklistInfo = (data: any, type: string = '') => {
  let checklist = null;
  if (data?.checklists?.length) {
    checklist = data.checklists[0];
  } else if (data?.itens) {
    checklist = data;
  }
  if (!checklist) return type === 'img' ? [] : '-';

  if (type === 'img') {
    if (!checklist?.itens) return [];
    return getChecklistFileEntries(checklist).map((item: any) => item.file_url);
  }
  if (type === 'inicio') {
    return checklist.started_at
      ? format(new Date(checklist.started_at), 'dd/MM/yyyy HH:mm')
      : '-';
  }
  if (type === 'criado') {
    return checklist.created_at
      ? format(new Date(checklist.created_at), 'dd/MM/yyyy HH:mm')
      : '-';
  }
  if (type === 'conclusão') {
    return checklist.finished_at
      ? format(new Date(checklist.finished_at), 'dd/MM/yyyy - HH:mm')
      : '-';
  }
  if (type === 'tempo') {
    if (checklist.started_at && checklist.finished_at) {
      const totalSeconds = differenceInSeconds(
        new Date(checklist.finished_at),
        new Date(checklist.started_at)
      );
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = Math.max(totalSeconds % 60, 0);
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} min`;
    } else {
      return '-';
    }
  }
  if (type === 'status') {
    if (checklist.status === 'approved') return 'Aprovado';
    if (checklist.status === 'rejected') return 'Reprovado';
    if (checklist.status === 'in_progress') return 'Em andamento';
    return checklist.status || '-';
  }
  if (type === 'tipo') {
    if (data.checklist?.name) {
      return data.checklist.name;
    }
    const slug = data.checklist?.slug;
    if (slug === 'checklist-veicular-entrada') {
      return 'Veicular Entrada';
    } else if (slug === 'checklist-veicular-saida') {
      return 'Veicular Saída';
    } else {
      return '-';
    }
  }
  if (type === 'transportadora') {
    return data.order?.carrier?.tradeName || '-';
  }
  if (type === 'placas') {
    const plates = [data.order?.vehiclePlate, data.order?.trailerPlate].filter(Boolean);
    return plates.length ? plates.join(' / ') : '-';
  }
  if (type === 'tipoVeiculo') {
    return data.order?.vehicleType?.name || '-';
  }
  if (type === 'agendamento') {
    if (!data.order?.scheduledAt) return '-';
    return format(new Date(data.order.scheduledAt), 'dd/MM/yyyy HH:mm');
  }
  if (type === 'motorista') {
    return data.order?.driverName || '-';
  }
  if (type === 'infoLinks') {
    const itens = getChecklistFileEntries(checklist);
    const links = itens.map((item: any) => item.file_url || item.fileUrl).filter(Boolean);

    return { itens, links };
  }
  if (type === 'user') {
    return checklist.user?.name || '-';
  }
  if (type === 'recusa') {
    const reasonReject = checklist.itens ? validarItens(checklist.itens) : [];
    if (reasonReject.length > 0) {
      return reasonReject.map(
        (item: any) => `${item.question_text} - ${item.answer}`
      );
    }
    return [];
  }
  // default
  return '-';
};
